import { useMemo } from 'react';
import { CButton, CBadge } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import CIcon from '@coreui/icons-react';
import { cilBan, cilEnvelopeClosed, cibAdobeAcrobatReader } from '@coreui/icons';
import { FormattedMessage } from 'react-intl';
import { formatDate } from 'utils/helpers';
import Pagination from 'components/common/Pagination';
import settings from 'config/settings';
import type { IntlShape } from 'react-intl';
import type { TFinancingRequest, TFinancingRequests } from 'hooks/useGetFinancingRequests';
import requireValue from 'utils/requireValue';
import s from './FinancingRequestsTable.module.scss';

/** A row of the financing requests table. */
interface IFinancingRequestRow extends Omit<TFinancingRequest, 'user' | 'created_at'> {
  /** Not selected by the query: always undefined. */
  _id: undefined;
  email: string;
  user: string;
  created_at: string;
}

interface IFinancingRequestsTableProps {
  t: IntlShape['formatMessage'];
  financingRequests: TFinancingRequests;
  showModal: (message: string) => void;
  changePage: (page: number) => void;
  currentPage: number;
  total: number | null;
}

const NoItemsView = () => (
  <div className={s.noItemsView}>
    <div className={s.text}>
      <FormattedMessage id="financing_requests.empty" />
    </div>
    <CIcon icon={cilBan} size="xxl" />
  </div>
);

const FinancingRequestsTable = ({
  t,
  financingRequests,
  showModal,
  changePage,
  currentPage,
  total,
}: IFinancingRequestsTableProps) => {
  const fields = useMemo(
    () => [
      { key: 'projectName', label: t({ id: 'financing_requests.project_name' }) },
      { key: 'email', label: t({ id: 'financing_requests.email' }) },
      { key: 'user', label: t({ id: 'financing_requests.user' }) },
      { key: 'created_at', label: t({ id: 'financing_requests.created_at' }) },
      { key: 'message', label: t({ id: 'financing_requests.message' }) },
      { key: 'pdf', label: 'PDF' },
    ],
    [t],
  );

  const items = useMemo(
    () =>
      // A missing list or item throws, as it did.
      requireValue(financingRequests, 'map').map((request): IFinancingRequestRow => {
        const {
          user: { email, firstName, lastName },
          created_at,
          ...rest
        } = requireValue(request, 'user');
        return {
          // The untyped code copied the `_id` the query does not select.
          _id: undefined,
          email,
          user: `${firstName} ${lastName}`,
          created_at: formatDate(created_at),
          // pdf: express_loan_id,
          ...rest,
        };
      }),
    [financingRequests],
  );

  return (
    <>
      <DataTable
        noItemsViewSlot={<NoItemsView />}
        items={items}
        fields={fields}
        size="sm"
        scopedSlots={{
          projectName: ({ projectName, archived }: IFinancingRequestRow) => (
            <td>
              <span>
                {projectName}{' '}
                {archived && (
                  <CBadge color="warning">
                    <FormattedMessage id="projects.archived" />
                  </CBadge>
                )}
              </span>
            </td>
          ),
          message: ({ message }: IFinancingRequestRow) => (
            <td>
              <CButton
                onClick={() => showModal(message)}
                style={{ backgroundColor: '#ddd', borderRadius: '5px' }}
              >
                <CIcon icon={cilEnvelopeClosed} style={{ marginTop: 0 }} />
              </CButton>
            </td>
          ),
          pdf: ({ express_loan_id }: IFinancingRequestRow) => {
            if (!express_loan_id) return <td />;
            return (
              <td>
                <a
                  href={`${settings.expressLoanPdf}?expressLoanId=${express_loan_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <CButton style={{ backgroundColor: '#ddd', borderRadius: '5px' }}>
                    <CIcon icon={cibAdobeAcrobatReader} style={{ marginTop: 0 }} />
                  </CButton>
                </a>
              </td>
            );
          },
          email: ({ email }: IFinancingRequestRow) => (
            <td>
              <a href={`mailto:${email}`}>{email}</a>
            </td>
          ),
        }}
      />
      <Pagination {...{ changePage, currentPage, total }} />
    </>
  );
};

export default FinancingRequestsTable;
